import type { ReactNode, Ref } from 'react';
import { Tabs } from '@mantine/core';
import clsx from 'clsx';
import { type CustomIconName, Icon } from '@/components/Icon/Icon';
import classes from './Tabs.module.css';

export interface TabProps {
  header: string;
  iconName?: CustomIconName;
  Component: ReactNode;
}
interface TabsProps {
  tabs: TabProps[];
  variant?: 'default' | 'drawer';
  isScrollable?: boolean;
  activeTab: string;
  setActiveTab: (e: string) => void;
  ref?: Ref<HTMLDivElement>;
}

export const TabsComponent = (props: TabsProps) => {
  const { variant = 'default', activeTab, setActiveTab, tabs, ref } = props;

  const Panel =
    props.tabs && props.tabs.find((tab) => tab.header === activeTab);

  const isDefault = variant === 'default';

  return tabs.length === 1 ? (
    Panel && Panel.Component
  ) : (
    <Tabs
      value={activeTab}
      onChange={(value) => {
        if (value !== null) setActiveTab(value);
      }}
      classNames={{
        root: clsx(
          classes.root,
          isDefault && !props.isScrollable && classes.rootSpaced
        ),
        tab: clsx(
          classes.tab,
          isDefault ? classes.tabDefault : classes.tabDrawer
        ),
        tabLabel: isDefault ? classes.tabLabelUppercase : undefined,
        panel: !isDefault ? classes.panelScrollable : classes.panel,
        list: classes.list,
      }}
    >
      <Tabs.List data-testid="tabs-list">
        {tabs.map((tab, index) => (
          <Tabs.Tab
            value={tab.header}
            key={index}
            leftSection={tab.iconName && <Icon size={16} name={tab.iconName} />}
          >
            {tab.header}
          </Tabs.Tab>
        ))}
      </Tabs.List>
      {activeTab && props.isScrollable ? (
        <div className={classes.tabsWrapper}>
          <div className={classes.header}></div>
          <Tabs.Panel value={activeTab} ref={ref} data-testid="tabs-panel">
            {Panel && Panel.Component}
          </Tabs.Panel>
          <div className={classes.footer}></div>
        </div>
      ) : (
        activeTab && (
          <Tabs.Panel value={activeTab} data-testid="tabs-panel">
            {Panel && Panel.Component}
          </Tabs.Panel>
        )
      )}
    </Tabs>
  );
};
